import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isUndefined, omitBy } from "lodash";
import { Observable } from "rxjs";
import {
  Campaign,
  CampaignCreate,
  CampaignUpdate
} from "src/app/backend/models/campaign";
import { ValidateType } from "src/app/decorators/validate-type.decorator";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageParams } from "src/app/support/page-params";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";

@Injectable()
export class CampaignBackend {
  private readonly http = inject(HttpClient);

  list(request?: PageRequest): Observable<PaginatedResult<Campaign>> {
    const params: Record<string, string | number | boolean> = omitBy(
      <PageParams<Campaign>>{
        s: request?.pageSize,
        p: request?.page,
        ...request?.filterValues,
        orderBy: "_id",
        sort: "desc"
      },
      isUndefined
    );

    return this.http.get<PaginatedResult<Campaign>>(
      `${environment.snoots.api.notification}/campaigns`,
      {
        params: params
      }
    );
  }

  loadById(id: string): Observable<Campaign> {
    return this.http.get<Campaign>(
      `${environment.snoots.api.notification}/campaigns/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Campagna creata"),
    errorMessage: marker("Impossibile creare la campagna")
  })
  create(payload: CampaignCreate): Observable<Campaign> {
    return this.http.post<Campaign>(
      `${environment.snoots.api.notification}/campaigns`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Campagna modificata"),
    errorMessage: marker("Impossibile modificare la campagna")
  })
  update(id: string, payload: CampaignUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.notification}/campaigns/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Email di test inviata"),
    errorMessage: marker("Impossibile inviare email di test")
  })
  test({
    id,
    email,
    customerId
  }: {
    id: string;
    email: string;
    customerId: string;
  }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.notification}/campaigns/${id}/test`,
      { email, customerId }
    );
  }

  @UserNotify({
    successMessage: marker("Campagna abilitata"),
    errorMessage: marker("Impossibile abilitare la campagna")
  })
  enable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.notification}/campaigns/${id}/enable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Campagna disabilitata"),
    errorMessage: marker("Impossibile disabilitare la campagna")
  })
  disable(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.notification}/campaigns/${id}/disable`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Campagna eliminata"),
    errorMessage: marker("Impossibile eliminare la campagna")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.notification}/campaigns/${id}`
    );
  }
}
